import { useState, useMemo, useRef } from "react";
import {
  X,
  Lock,
  Calendar,
  Loader2,
  DollarSign,
  RotateCcw,
} from "lucide-react";
import { VaultConfirmModal } from "./vault/VaultConfirmModal";
import { formatCurrency } from "../lib/calculations";
import { useToast } from "./ui/ToastModal";
import {
  type PaymentLineItem,
  type PaymentAction,
  buildPaymentReconciliationInput,
  mergePaymentLineItemsByType,
  savePaymentReconciliation,
  getActionColor,
} from "../lib/paymentLineItems";
import { logActivity } from "../lib/activity-logger";
import {
  type ClaimsReportRow,
  type RetentionLineItem,
  MONTH_SHORT,
} from "../lib/paymentPanelShared.types";
import { ActionDropdown } from "../lib/paymentPanelShared";
import {
  calculateDaysBeforeDue,
  dateOnlyToUtcIso,
  stableStringify,
} from "../lib/paymentVersions";
import { logError } from "../lib/logError";

// ─── Props ────────────────────────────────────────────────────
interface RetentionPaymentPanelProps {
  report: ClaimsReportRow;
  existingLineItems: PaymentLineItem[];
  retentionAvailableAmount?: number;
  onClose: () => void;
  onSaved: () => void;
}

// ─── Component ────────────────────────────────────────────────
export function RetentionPaymentPanel({
  report,
  existingLineItems,
  retentionAvailableAmount,
  onClose,
  onSaved,
}: RetentionPaymentPanelProps) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const retentionClaimed = Math.round(
    Math.max(
      0,
      retentionAvailableAmount ?? Number(report.retention_release_amount ?? 0),
    ) * 100,
  ) / 100;
  const currentReportRelease = Math.round(Number(report.retention_release_amount ?? 0) * 100) / 100;
  const period = `${MONTH_SHORT[report.claim_month - 1]} ${report.claim_year}`;

  // ─── Retention Line Item ───────────────────────────────────
  const [retentionLine, setRetentionLine] = useState<RetentionLineItem>(() => {
    const existing = existingLineItems.find((li) => li.line_type === "retention");
    if (existing) {
      return {
        claimed_amount: retentionClaimed,
        paid_amount: existing.paid_amount,
        action: existing.action,
        agreed_amount: existing.agreed_amount ?? null,
        notes: existing.notes ?? "",
      };
    }
    return {
      claimed_amount: retentionClaimed,
      paid_amount: 0,
      action: "pending" as PaymentAction,
      agreed_amount: null,
      notes: "",
    };
  });
  const [datePaid, setDatePaid] = useState(() =>
    report.retention_received_at?.slice(0, 10) || report.date_paid || ""
  );

  // ─── Computed ─────────────────────────────────────────────
  const paidTotal = useMemo(() => {
    if (retentionLine.action !== "paid") return 0;
    return Math.round(Math.min(Math.max(0, retentionLine.paid_amount), retentionClaimed) * 100) / 100;
  }, [retentionLine, retentionClaimed]);

  const remaining = Math.max(0, retentionClaimed - paidTotal);
  const paymentState = useMemo(
    () => stableStringify({ datePaid, retentionLine }),
    [datePaid, retentionLine]
  );
  const initialPaymentState = useRef(paymentState);
  const isDirty = paymentState !== initialPaymentState.current;


  // ─── Reset ────────────────────────────────────────────────
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const executeReset = async () => {
    setShowResetConfirm(false);
    setSaving(true);
    try {
      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "retention",
        []
      );
      const resetPayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid: report.date_paid,
        daysBeforeDue: report.days_before_due,
        retentionReceivedAmount: null,
        retentionReceivedAt: null,
        retentionReceivedNotes: null,
        changesSummary: `Retention payment reset for Claim #${report.claim_number} (${period}).`,
      });

      const { success, error: resetError } = await savePaymentReconciliation(resetPayload);
      if (!success) throw new Error(resetError);

      addToast({
        type: "success",
        title: "Payment Reset",
        message: "Retention payment data has been cleared.",
      });

      // Close panel and refresh parent data so next open gets clean state
      onSaved();
    } catch (err) {
      logError("Error resetting retention payment:", err);
      addToast({
        type: "error",
        title: "Reset Failed",
        message: err instanceof Error ? err.message : "Could not reset payment",
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty) {
      addToast({ type: "info", title: "No changes to save" });
      return;
    }



    setSaving(true);
    try {
      // Skip if no decision made yet
      if (retentionLine.action === "pending") {
        addToast({ type: "info", title: "No action selected", message: "Select an action before saving." });
        setSaving(false);
        return;
      }

      // For "paid": if user didn't manually enter amount, auto-fill = claimed
      const requestedPaidAmount = retentionLine.action === "paid" && retentionLine.paid_amount === 0
        ? retentionClaimed : retentionLine.paid_amount;
      const paidAmount = Math.round(Math.min(Math.max(0, requestedPaidAmount), retentionClaimed) * 100) / 100;

      // Build single retention line item
      const lineItem: PaymentLineItem = {
        report_id: report.id,
        line_type: "retention",
        claimed_percentage: 100,
        claimed_amount: retentionClaimed,
        paid_percentage:
          retentionLine.action === "voided" || retentionLine.action === "held"
            ? 0
            : paidAmount > 0
              ? Math.round((paidAmount / retentionClaimed) * 100 * 100) / 100
              : 0,
        paid_amount:
          retentionLine.action === "voided" || retentionLine.action === "held" ? 0 : paidAmount,
        action: retentionLine.action,
        agreed_amount:
          retentionLine.action === "agreement_made" ? retentionLine.paid_amount : null,
        notes: retentionLine.notes || null,
      };

      // 1. Save the merged payment state and payment version in one RPC.
      const effectivePaid = retentionLine.action === "paid" ? paidAmount : 0;
      const mergedLineItems = mergePaymentLineItemsByType(
        existingLineItems,
        "retention",
        retentionClaimed > 0 ? [lineItem] : []
      );
      const savePayload = buildPaymentReconciliationInput({
        report,
        lineItems: mergedLineItems,
        datePaid,
        daysBeforeDue: calculateDaysBeforeDue(datePaid, report.payment_due_date),
        retentionReceivedAmount: effectivePaid > 0 ? effectivePaid : null,
        retentionReceivedAt: effectivePaid > 0 ? dateOnlyToUtcIso(datePaid) : null,
        retentionReceivedNotes:
          effectivePaid > 0 ? retentionLine.notes?.trim() || null : null,
        changesSummary: `Retention payment for Claim #${report.claim_number} (${period}): ${retentionLine.action}: ${formatCurrency(effectivePaid, true)} of ${formatCurrency(retentionClaimed, true)}.`,
      });

      const { success, error: saveError } = await savePaymentReconciliation(savePayload);
      if (!success) throw new Error(saveError);

      // 2. Activity logging
      const label =
        retentionLine.action === "paid" ? "Paid"
          : retentionLine.action === "voided" ? "Voided"
            : retentionLine.action === "held" ? "Held"
              : retentionLine.action === "agreement_made" ? "Agreement Made"
                : "Works Not Completed";

      await logActivity({
        projectId: report.project_id,
        actionType: "UPDATE",
        entityType: "PAYMENT",
        entityId: report.id,
        description: `Retention payment for Claim #${report.claim_number} (${period}): ${label}: ${formatCurrency(paidTotal, true)} of ${formatCurrency(retentionClaimed, true)}`,
        metadata: {
          claim_number: report.claim_number,
          panel: "retention",
          retention_claimed: retentionClaimed,
          retention_paid: paidTotal,
          action: retentionLine.action,
        },
      });

      addToast({
        type: "success",
        title: "Retention Payment Saved",
        message: `${label}: ${formatCurrency(paidTotal, true)}`,
      });
      onSaved();
    } catch (err) {
      logError("Error saving retention payment:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message: err instanceof Error ? err.message : "Could not save retention payment",
      });
    } finally {
      setSaving(false);
    }
  };

  if (retentionClaimed <= 0) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: 20,
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            width: "100%",
            maxWidth: 420,
            padding: "32px 28px",
            textAlign: "center",
            boxShadow: "0 25px 60px rgba(0,0,0,0.2)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Lock size={28} color="#94a3b8" />
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: "#0f172a",
              marginTop: 12,
            }}
          >
            No Retention Balance
          </div>
          <div
            style={{
              fontSize: "0.82rem",
              color: "#64748b",
              marginTop: 6,
              marginBottom: 20,
            }}
          >
            No retention amount was held for this claim period.
          </div>
          <button
            onClick={onClose}
            style={{
              padding: "8px 24px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          width: "100%",
          maxWidth: 440,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(217,119,6,0.06)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "linear-gradient(135deg, #fbbf24, #d97706)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(217,119,6,0.3)",
              }}
            >
              <Lock size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
                Retention Payment
              </div>
              <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                Claim #{report.claim_number} • {period}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              padding: 6,
              borderRadius: 8,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* Retention balance (read-only) */}
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "0.68rem",
                  color: "#92400e",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontWeight: 600,
                }}
              >
                Outstanding Retention Balance
              </div>
              <div data-seq-private=""
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  color: "#92400e",
                  marginTop: 3,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatCurrency(retentionClaimed, true)}
              </div>
              {currentReportRelease > 0 && (
                <div data-seq-private="" style={{ fontSize: "0.7rem", color: "#92400e", marginTop: 4 }}>
                  This report release line: {formatCurrency(currentReportRelease, true)}
                </div>
              )}
            </div>
            <DollarSign size={22} style={{ color: "#d97706", opacity: 0.4 }} />
          </div>

          {/* Action selector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
              padding: "12px 14px",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              background:
                retentionLine.action !== "paid"
                  ? getActionColor(retentionLine.action).bg
                  : "#f8fafc",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  marginBottom: 4,
                }}
              >
                Payment Status
              </div>
              <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#0f172a" }}>
                Paid:{" "}
                <span data-seq-private=""
                  style={{
                    color:
                      retentionLine.action === "voided" || retentionLine.action === "held"
                        ? "#dc2626"
                        : paidTotal >= retentionClaimed
                          ? "#16a34a"
                          : "#d97706",
                    fontWeight: 700,
                  }}
                >
                  {retentionLine.action === "voided" || retentionLine.action === "held" ? "$0.00" : formatCurrency(paidTotal, true)}
                </span>
              </div>
            </div>
            <ActionDropdown
              value={retentionLine.action}
              onChange={(action) => {
                const updated = { ...retentionLine, action };
                if (action === "voided" || action === "held" || action === "works_not_completed") {
                  updated.paid_amount = 0;
                } else if (action === "paid") {
                  // Auto-fill only when coming from zeroed state (allows overpayment)
                  if (retentionLine.paid_amount === 0 || retentionLine.action === "voided" || retentionLine.action === "held" || retentionLine.action === "works_not_completed" || retentionLine.action === "pending") {
                    updated.paid_amount = retentionClaimed;
                  }
                }
                setRetentionLine(updated);
              }}
            />
          </div>

          {/* Reset button */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setShowResetConfirm(true)}
              disabled={saving}
              style={{
                padding: "3px 10px",
                borderRadius: 6,
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#64748b",
                fontSize: "0.68rem",
                fontWeight: 500,
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                opacity: saving ? 0.5 : 1,
              }}
            >
              <RotateCcw size={10} /> Reset
            </button>
          </div>

          {/* Paid amount input (for "paid", "pending", or "agreement_made" action) */}
          {(retentionLine.action === "paid" || retentionLine.action === "pending" || retentionLine.action === "agreement_made") && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                background: "#f0fdf4",
                borderRadius: 12,
                marginBottom: 16,
                border: "1px solid #bbf7d0",
              }}
            >
              <DollarSign size={16} color="#16a34a" />
              <div>
                <div style={{ fontSize: "0.68rem", color: "#16a34a", fontWeight: 600, marginBottom: 4 }}>
                  Amount Received
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>$</span>
                  <input data-seq-private
                    type="number"
                    value={retentionLine.paid_amount}
                    disabled={false}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const val = raw === "" ? 0 : parseFloat(raw);
                      if (isNaN(val)) return;
                      setRetentionLine({
                        ...retentionLine,
                        paid_amount: Math.min(Math.max(0, val), retentionClaimed),
                      });
                    }}
                    style={{
                      width: 120,
                      padding: "5px 8px",
                      border: "1px solid #bbf7d0",
                      borderRadius: 6,
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      outline: "none",
                    }}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <span data-seq-private="" style={{ fontSize: "0.68rem", color: "#64748b" }}>
                    of {formatCurrency(retentionClaimed, true)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Date Paid */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Calendar size={13} color="#64748b" />
              <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}>
                Date Paid
              </span>
            </div>
            <input
              type="date"
              value={datePaid}
              onChange={(e) => setDatePaid(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                fontSize: "0.82rem",
                color: "#0f172a",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>



          {/* Remaining feedback */}
          {remaining > 0 && retentionLine.action !== "voided" && retentionLine.action !== "held" && (
            <div data-seq-private=""
              style={{
                fontSize: "0.72rem",
                color: "#d97706",
                fontWeight: 600,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              {formatCurrency(remaining, true)} still outstanding
            </div>
          )}
          {paidTotal >= retentionClaimed && retentionLine.action !== "voided" && retentionLine.action !== "held" && (
            <div
              style={{
                fontSize: "0.72rem",
                color: "#16a34a",
                fontWeight: 600,
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              ✓ Full retention amount covered
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            style={{
              padding: "8px 20px",
              borderRadius: 10,
              border: "none",
              background: saving || !isDirty
                ? "#94a3b8"
                : "linear-gradient(135deg, #fbbf24, #d97706)",
              color: "#fff",
              fontSize: "0.82rem",
              fontWeight: 600,
              cursor: saving || !isDirty ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: saving ? "none" : "0 2px 8px rgba(217,119,6,0.3)",
            }}
          >
            {saving && <Loader2 size={14} className="spin" />}
            {saving
              ? "Saving..."
              : !isDirty
                ? "No Changes"
                : "Save Retention"}
          </button>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <VaultConfirmModal
        isOpen={showResetConfirm}
        title="Reset Retention Payment"
        message="This will delete the saved retention payment data. You can re-enter payment data after resetting."
        confirmLabel="Reset"
        variant="warning"
        onConfirm={executeReset}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
}
